/* site/SiteLayout.jsx — the public site's shell, mounted at "/" (2026-09-07:
 * lhc-site/*.html ported into the platform; it IS the main site now). Owns
 * the scoped stylesheet, the chrome, scroll/hash handling, the `.reveal`
 * intersection choreography and the content provider. */
import React, { useCallback, useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import "./site.css";
import { SiteContentProvider } from "./content/SiteContent";
import { EditProvider, wantsEdit } from "./edit/EditContext";
import SiteEditLayer from "./edit/SiteEditLayer";
import { SiteHeader, SearchOverlay, SiteFooter } from "./SiteChrome";
import { SiteLangContext } from "./siteLangContext";
import { SITE_LANGS, getStoredLang, storeLang, loadDict, watchLang } from "./siteLang";

const BODY_BG = "#F8F6F2";

function useSiteDocument() {
  useEffect(() => {
    const html = document.documentElement, body = document.body;
    const prev = { bg: body.style.background, sb: html.style.scrollBehavior, theme: html.getAttribute("data-theme") };
    const prevInline = { htmlH: html.style.height, htmlOy: html.style.overflowY, htmlOx: html.style.overflowX, bodyH: body.style.height, bodyO: body.style.overflow };
    body.style.background = BODY_BG;
    html.style.scrollBehavior = "smooth";
    /* the app shell locks html/body to the viewport (configurator layout); the site is a normal
       scrolling document — inline wins over the shell's stylesheet, site.css backs it up */
    html.style.height = "auto"; html.style.overflowY = "auto"; html.style.overflowX = "hidden";
    body.style.height = "auto"; body.style.overflow = "visible";
    body.setAttribute("data-lhc-site", "1");
    return () => {
      body.style.background = prev.bg; html.style.scrollBehavior = prev.sb;
      html.style.height = prevInline.htmlH; html.style.overflowY = prevInline.htmlOy; html.style.overflowX = prevInline.htmlOx;
      body.style.height = prevInline.bodyH; body.style.overflow = prevInline.bodyO;
      body.removeAttribute("data-lhc-site"); body.removeAttribute("data-nav-open");
    };
  }, []);
}

/* scroll to top on every route change; to the hash target when there is one
   (retrying briefly because content may still be rendering) */
function useScrollRestore() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) { window.scrollTo({ top: 0, left: 0, behavior: "auto" }); return undefined; }
    let tries = 0, t = null;
    const go = () => {
      const el = document.getElementById(hash.slice(1));
      if (el) { el.scrollIntoView({ block: "start", behavior: "smooth" }); return; }
      if (tries++ < 20) t = setTimeout(go, 100);
    };
    t = setTimeout(go, 30);
    return () => clearTimeout(t);
  }, [pathname, hash]);
}

/* the prototype's IntersectionObserver reveal, made page-agnostic: any `.reveal`
   that appears in the site (now or later) gets `is-visible` when it scrolls in */
function useReveal(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    if (!("IntersectionObserver" in window)) { root.querySelectorAll(".reveal").forEach((el) => el.classList.add("is-visible")); return undefined; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add("is-visible"); io.unobserve(en.target); } });
    }, { threshold: 0.14, rootMargin: "0px 0px -6% 0px" });
    const attach = () => root.querySelectorAll(".reveal:not([data-rv])").forEach((el) => { el.setAttribute("data-rv", "1"); io.observe(el); });
    attach();
    const mo = new MutationObserver(attach);
    mo.observe(root, { childList: true, subtree: true });
    /* drop the markers too: without this a remount (StrictMode's double-mount in dev, or any
       re-render of the shell) leaves every .reveal marked but observed by the disconnected
       observer, so nothing ever becomes visible */
    return () => { mo.disconnect(); io.disconnect(); root.querySelectorAll("[data-rv]").forEach((el) => el.removeAttribute("data-rv")); };
  }, [rootRef]);
}

/* EN · IT · DE · FR. The dictionary for the chosen language is fetched on demand and applied to the
   site root; English needs no dictionary and no download. Only the public site is translated — the
   signed-in apps have their own engine (src/i18n). */
function useSiteLanguage(rootRef) {
  const [lang, setLangState] = useState(getStoredLang);
  useEffect(() => {
    let alive = true;
    let stop = () => {};
    document.documentElement.setAttribute("data-site-lang", lang);
    loadDict(lang).then((dict) => {
      if (!alive) return;
      stop = watchLang(rootRef.current, dict);
    });
    return () => { alive = false; stop(); };
  }, [lang, rootRef]);
  const setLang = useCallback((next) => {
    if (SITE_LANGS.indexOf(next) < 0) return;
    storeLang(next);
    setLangState(next);
  }, []);
  return [lang, setLang];
}

/* The visual editor loads ONLY when the page was opened with ?edit=1 — its code, and the whole
   content index it needs, stay out of every ordinary visit. */
function SiteShell() {
  const rootRef = React.useRef(null);
  const [search, setSearch] = useState(false);
  const [lang, setLang] = useSiteLanguage(rootRef);
  useSiteDocument();
  useScrollRestore();
  useReveal(rootRef);
  const closeSearch = useCallback(() => setSearch(false), []);
  const { pathname } = useLocation();
  /* Every page runs the same prototype chrome (site.css). The home film page is the only one
     whose header floats transparent over a dark hero and whose footer drops the statement row;
     `is-home` additionally scopes its full-viewport section rules (pages/home.css) to the root,
     so `main > section` still matches as the prototype's stylesheet expects. */
  const home = pathname === "/";
  return (
    <SiteLangContext.Provider value={React.useMemo(() => ({ lang, setLang }), [lang, setLang])}>
    <SiteContentProvider>
      <div className={"lhc-site" + (home ? " is-home" : "")} ref={rootRef} data-testid="lhc-site" data-header={home ? "hero" : "solid"}>
        <SiteHeader home={home} onSearch={() => setSearch(true)} />
        <SearchOverlay open={search} onClose={closeSearch} />
        <main>
          <Outlet />
        </main>
        <SiteFooter home={home} />
        <SiteEditLayer rootRef={rootRef} />
      </div>
    </SiteContentProvider>
    </SiteLangContext.Provider>
  );
}

export default function SiteLayout() {
  /* ?edit=1 only ARMS the editor; EditProvider still requires a signed-in superadmin */
  if (!wantsEdit()) return <SiteShell />;
  return (
    <EditProvider>
      <SiteShell />
    </EditProvider>
  );
}

/** document <title> + meta description per page (restored when the site unmounts) */
function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  const created = !el;
  const prev = el ? el.getAttribute("content") : null;
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
  return () => { if (created) el.remove(); else if (prev != null) el.setAttribute("content", prev); };
}

/** document <title>, meta description and the prototype's og:/twitter: tags per page (restored on unmount) */
export function usePageMeta(meta) {
  const title = meta && meta.title, description = meta && meta.description, image = meta && (meta.image || meta.ogImage);
  /* every prototype page seeds og:image and twitter:image with DIFFERENT placeholders
     (…-101 / …-102); a page that supplies only `image` keeps them identical. */
  const twitterImage = (meta && meta.twitterImage) || image;
  useEffect(() => {
    if (!title) return undefined;
    const prevTitle = document.title;
    document.title = title;
    const undo = [];
    if (description) undo.push(setMeta("name", "description", description));
    undo.push(setMeta("property", "og:type", "website"), setMeta("property", "og:title", title), setMeta("name", "twitter:card", "summary_large_image"), setMeta("name", "twitter:title", title));
    if (description) undo.push(setMeta("property", "og:description", description), setMeta("name", "twitter:description", description));
    if (image) undo.push(setMeta("property", "og:image", image));
    if (twitterImage) undo.push(setMeta("name", "twitter:image", twitterImage));
    return () => { document.title = prevTitle; undo.reverse().forEach((fn) => fn()); };
  }, [title, description, image, twitterImage]);
}

/** on-brand 404 inside the site shell */
export function SiteNotFound() {
  usePageMeta({ title: "Page not found | Lugano Home Collection" });
  return (
    <section className="section wrap" style={{ paddingTop: "calc(var(--header-h) + var(--sp-8))" }}>
      <p className="eyebrow">404</p>
      <h1 className="display-lg" style={{ marginTop: "var(--sp-3)" }}>This page does not exist.</h1>
      <p className="body-md" style={{ marginTop: "var(--sp-4)", maxWidth: "44ch" }}>The address may be out of date. Continue to the collection, or contact LHC.</p>
      <div style={{ marginTop: "var(--sp-6)", display: "flex", gap: "var(--sp-4)", flexWrap: "wrap" }}>
        <a href="/" className="btn btn--solid">Back to the site</a>
        <a href="/contact" className="btn btn--outline">Contact LHC</a>
      </div>
    </section>
  );
}
